import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { collection, query, orderBy, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { History, RotateCcw, Eye, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";

const translations = {
  en: {
    versionHistory: "Version History",
    version: "Version",
    current: "Current",
    restore: "Restore",
    preview: "Preview",
    noVersions: "No previous versions",
    restoreConfirm: "Restore this version?",
    restoreDescription: "This will replace the current content with this version. A backup of the current version will be saved.",
    cancel: "Cancel",
    confirm: "Restore",
    savedBy: "Saved",
    autoSave: "Auto-save",
    manualSave: "Manual save",
    aiGenerated: "AI Generated",
  },
  fr: {
    versionHistory: "Historique des versions",
    version: "Version",
    current: "Actuelle",
    restore: "Restaurer",
    preview: "Aperçu",
    noVersions: "Aucune version précédente",
    restoreConfirm: "Restaurer cette version ?",
    restoreDescription: "Cela remplacera le contenu actuel par cette version. Une sauvegarde de la version actuelle sera créée.",
    cancel: "Annuler",
    confirm: "Restaurer",
    savedBy: "Enregistré",
    autoSave: "Auto-sauvegarde",
    manualSave: "Sauvegarde manuelle",
    aiGenerated: "Généré par IA",
  },
};

export interface CIMVersion {
  id: string;
  versionNumber: number;
  content: {
    executiveSummary: string;
    businessOverview: string;
    financialHighlights: string;
    growthOpportunities: string;
    investmentConsiderations: string;
    transitionPlan: string;
  };
  financialSummary: {
    reportedEbitda: number;
    totalAddbacks: number;
    adjustedEbitda: number;
    ebitdaMultiple: number;
    valuationLow: number;
    valuationMid: number;
    valuationHigh: number;
  };
  savedAt: Date;
  savedBy?: string;
  saveType: "manual" | "auto" | "ai_generated";
  notes?: string;
}

interface CIMVersionHistoryProps {
  dealId: string;
  onRestore: (version: CIMVersion) => void;
}

export function CIMVersionHistory({ dealId, onRestore }: CIMVersionHistoryProps) {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const locale = language === "fr" ? fr : enUS;

  const [versions, setVersions] = useState<CIMVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<CIMVersion | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<CIMVersion | null>(null);

  useEffect(() => {
    if (open && dealId) {
      loadVersions();
    }
  }, [open, dealId]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const versionsRef = collection(db, "cimData", dealId, "versions");
      const q = query(versionsRef, orderBy("versionNumber", "desc"));
      const snapshot = await getDocs(q);

      const loadedVersions: CIMVersion[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        savedAt: doc.data().savedAt?.toDate() || new Date(),
      })) as CIMVersion[];

      setVersions(loadedVersions);
    } catch (error) {
      console.error("Error loading versions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreClick = (version: CIMVersion) => {
    setSelectedVersion(version);
    setRestoreDialogOpen(true);
  };

  const handleConfirmRestore = () => {
    if (selectedVersion) {
      onRestore(selectedVersion);
      setRestoreDialogOpen(false);
      setOpen(false);
    }
  };

  const getSaveTypeBadge = (type: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      manual: "default",
      auto: "secondary",
      ai_generated: "outline",
    };
    const labels: Record<string, string> = {
      manual: t.manualSave,
      auto: t.autoSave,
      ai_generated: t.aiGenerated,
    };
    return <Badge variant={variants[type] || "secondary"}>{labels[type] || type}</Badge>;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <History className="h-4 w-4 mr-2" />
            {t.versionHistory}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t.versionHistory}</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t.noVersions}
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {versions.map((version, index) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">
                          {t.version} {version.versionNumber}
                        </span>
                        {index === 0 && (
                          <Badge variant="default" className="bg-green-500">
                            {t.current}
                          </Badge>
                        )}
                        {getSaveTypeBadge(version.saveType)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t.savedBy}{" "}
                        {formatDistanceToNow(version.savedAt, {
                          addSuffix: true,
                          locale,
                        })}
                      </p>
                      {version.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {version.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewVersion(version)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {index !== 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreClick(version)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          {t.restore}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewVersion} onOpenChange={() => setPreviewVersion(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {t.version} {previewVersion?.versionNumber} - {t.preview}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[500px]">
            {previewVersion && (
              <div className="space-y-4 pr-4">
                <div>
                  <h3 className="font-semibold mb-2">Executive Summary</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {previewVersion.content.executiveSummary}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Business Overview</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {previewVersion.content.businessOverview}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Financial Highlights</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {previewVersion.content.financialHighlights}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Growth Opportunities</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {previewVersion.content.growthOpportunities}
                  </p>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.restoreConfirm}</AlertDialogTitle>
            <AlertDialogDescription>{t.restoreDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRestore}>
              {t.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
